import type { ComponentType } from 'react';
import {
  Cloud,
  CloudDrizzle,
  CloudFog,
  CloudHail,
  CloudLightning,
  CloudMoon,
  CloudRain,
  CloudSnow,
  CloudSun,
  ChevronDown,
  Droplets,
  MapPin,
  Moon,
  Plus,
  Sun,
  Wind,
  type LucideProps,
} from 'lucide-react';
import { MAX_LOCATIONS, type WeatherResponse, type WidgetLocation } from '@omnia/shared';
import { tr } from '../../lib/i18n.ts';
import { Popover } from '../Popover.tsx';
import { openSettings } from '../../settings/store.ts';
import { useShows } from '../BarPlanContext.tsx';
import { makePrimary } from '../locations.ts';
import { useInfoBarPrefs, usePrefsStore } from '../prefsStore.ts';
import { useWeather } from '../queries.ts';
import { describeCondition, hourLabel, pickForecastChips, summarizePrecipitation, type ConditionIcon } from '../weather.ts';
import { StaleMark } from './StaleMark.tsx';
import type { WidgetVariant } from './types.ts';

const ICONS: Record<ConditionIcon, ComponentType<LucideProps>> = {
  sun: Sun,
  moon: Moon,
  'cloud-sun': CloudSun,
  'cloud-moon': CloudMoon,
  cloud: Cloud,
  fog: CloudFog,
  drizzle: CloudDrizzle,
  rain: CloudRain,
  snow: CloudSnow,
  storm: CloudLightning,
  hail: CloudHail,
};

const degrees = (value: number) => `${Math.round(value)}°`;

export function ConditionIconView({ code, isDay, size = 18 }: { code: number; isDay: boolean; size?: number }) {
  const condition = describeCondition(code, isDay);
  const Icon = ICONS[condition.icon];
  return <Icon size={size} role="img" aria-label={condition.label} data-icon={condition.icon} />;
}

export function WeatherWidget({ variant }: { variant: WidgetVariant }) {
  const { locations } = useInfoBarPrefs();
  const location = locations[0]!;
  const others = locations.slice(1);
  const weather = useWeather(location, true);
  const data = weather.data;
  const shows = useShows();
  if (variant === 'bar' && !shows('location') && !shows('now')) return null;

  return (
    <div className="iw iw-weather" data-variant={variant}>
      {(variant === 'sheet' || shows('location')) && (
      <span className="iw-piece" data-piece="location">
      <Popover
        label={`${tr.infobar.changeLocation}: ${location.name}`}
        triggerClassName="iw-location"
        align="start"
        trigger={
          <>
            <MapPin size={14} aria-hidden />
            <span className="iw-location-name">{location.name}</span>
            <ChevronDown size={12} aria-hidden />
          </>
        }
      >
        {(close) => <CityMenu onDone={close} />}
      </Popover>
      </span>
      )}
      {data ? (
        <>
          {(variant === 'sheet' || shows('now')) && (
          <span className="iw-now" data-piece="now" title={describeCondition(data.current.weatherCode, data.current.isDay).label}>
            <ConditionIconView code={data.current.weatherCode} isDay={data.current.isDay} size={20} />
            <span className="iw-temp">{Math.round(data.current.temperature)}°C</span>
            {weather.stale && <StaleMark />}
          </span>
          )}
          {(variant === 'sheet' || shows('wind')) && (
          <span className="iw-fact iw-wind" data-piece="wind" title={tr.infobar.windLabel}>
            <Wind size={15} aria-hidden />
            <span className="visually-hidden">{tr.infobar.windLabel}: </span>
            {tr.infobar.wind(data.current.windKmh)}
          </span>
          )}
          {(variant === 'sheet' || shows('rain')) && <Precipitation weather={data} />}
          {(variant === 'sheet' || shows('hourly')) && (
          <ol className="iw-hourly" data-piece="hourly" aria-label={tr.infobar.widget.weather}>
            {pickForecastChips(data.hourly, data.current.time).map((hour) => {
              const label = describeCondition(hour.weatherCode, hour.isDay).label;
              return (
                <li key={hour.time} className="iw-chip" title={tr.infobar.forecastAt(hourLabel(hour.time), degrees(hour.temperature), label)}>
                  <span className="iw-chip-hour">{hourLabel(hour.time)}</span>
                  <ConditionIconView code={hour.weatherCode} isDay={hour.isDay} size={14} />
                  <span className="iw-chip-temp">{degrees(hour.temperature)}</span>
                </li>
              );
            })}
          </ol>
          )}
          {others.length > 0 && (variant === 'sheet' || shows('cities')) && (
            <ul className="iw-cities" aria-label={tr.infobar.savedCities}>
              {others.map((place, index) => (
                <CityChip key={`${place.lat},${place.lon}`} place={place} onShow={() => usePrefsStore.getState().update({ locations: makePrimary(locations, index + 1) })} />
              ))}
            </ul>
          )}
        </>
      ) : weather.isError ? (
        <button type="button" className="iw-retry" onClick={() => weather.refetch()}>
          {tr.infobar.weatherUnavailable} · {tr.common.retry}
        </button>
      ) : (
        <span className="iw-loading" aria-busy="true">
          {tr.infobar.loadingShort}
        </span>
      )}
    </div>
  );
}

/** A saved city other than the primary one, as a compact chip that makes it the displayed city when clicked. */
function CityChip({ place, onShow }: { place: WidgetLocation; onShow: () => void }) {
  const weather = useWeather(place, true);
  const data = weather.data;
  const details = data
    ? [
        `${Math.round(data.current.temperature)}°C`,
        describeCondition(data.current.weatherCode, data.current.isDay).label,
        tr.infobar.wind(data.current.windKmh),
        summarizePrecipitationText(data),
      ].join(' · ')
    : tr.infobar.loadingShort;
  return (
    <li data-piece="cities">
      <button type="button" className="iw-city" title={tr.infobar.cityChip(place.name, details)} aria-label={`${tr.infobar.showCity(place.name)}. ${details}`} onClick={onShow}>
        {data ? <ConditionIconView code={data.current.weatherCode} isDay={data.current.isDay} size={15} /> : <MapPin size={14} aria-hidden />}
        <span className="iw-city-name">{place.name}</span>
        <span className="iw-city-temp">{data ? degrees(data.current.temperature) : tr.infobar.loadingShort}</span>
        {weather.stale && <StaleMark />}
      </button>
    </li>
  );
}

/** The saved cities to pick from, and a search to add another while there is room. */
function CityMenu({ onDone }: { onDone: () => void }) {
  const { locations } = useInfoBarPrefs();
  const { update } = usePrefsStore.getState();
  return (
    <div className="city-menu">
      <ul className="city-list" aria-label={tr.infobar.savedCities}>
        {locations.map((place, index) => (
          <li key={`${place.lat},${place.lon}`}>
            <button
              type="button"
              className="city-item"
              aria-current={index === 0 || undefined}
              onClick={() => {
                update({ locations: makePrimary(locations, index) });
                onDone();
              }}
            >
              <MapPin size={14} aria-hidden />
              <span className="city-item-name">{place.name}</span>
              {index === 0 && <span className="k-badge">{tr.infobar.primaryCity}</span>}
            </button>
          </li>
        ))}
      </ul>
      {locations.length < MAX_LOCATIONS ? (
        <button
          type="button"
          className="city-item city-add"
          onClick={() => {
            onDone();
            openSettings('konumlar');
          }}
        >
          <Plus size={14} aria-hidden />
          <span className="city-item-name">{tr.infobar.addCityMenu}</span>
        </button>
      ) : (
        <p className="s-hint">{tr.infobar.cityLimit(MAX_LOCATIONS)}</p>
      )}
    </div>
  );
}

function summarizePrecipitationText(weather: WeatherResponse): string {
  const summary = summarizePrecipitation(weather);
  return summary.kind === 'now'
    ? tr.infobar.rainNow(new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 1 }).format(summary.mm))
    : summary.kind === 'chance'
      ? tr.infobar.rainChance(summary.percent)
      : tr.infobar.noRain;
}

function Precipitation({ weather }: { weather: NonNullable<ReturnType<typeof useWeather>['data']> }) {
  const summary = summarizePrecipitation(weather);
  const text =
    summary.kind === 'now'
      ? tr.infobar.rainNow(new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 1 }).format(summary.mm))
      : summary.kind === 'chance'
        ? tr.infobar.rainChance(summary.percent)
        : tr.infobar.noRain;
  return (
    <span className="iw-fact iw-rain" data-piece="rain">
      <Droplets size={15} aria-hidden />
      {text}
    </span>
  );
}
